import type { CalendarStrings } from '@fluentui/react-datepicker-compat';
import { DatePicker, DatePickerProps } from '@fluentui/react-datepicker-compat';
import { isPlainObject } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';

import { useInputAppearance } from '../hooks/useInputAppearance';
import { useLocalizedDatepicker } from '../hooks/useLocalizedDatepicker';
import './LocalizedDatePicker.scss';

/**
 * Props accepted by LocalizedDatePicker.
 *
 * Supports both the legacy northstar-style prop names (selectedDate,
 * onDateChange, allowManualInput, defaultSelectedDate, formatMonthDayYear,
 * input) and the native v9 DatePicker props (value, onSelectDate,
 * allowTextInput, formatDate, etc.).
 *
 * Legacy props take precedence when both are provided.
 */
export interface LocalizedDatePickerProps extends Omit<DatePickerProps, 'strings' | 'input'> {
  id?: string;
  clearable?: boolean;

  // --- Legacy northstar-compatible props (mapped to v9 equivalents internally) ---

  /** @deprecated Use `value` instead. Sets the selected date. */
  selectedDate?: Date;
  /** @deprecated Use `value` instead. Sets the initial selected date. */
  defaultSelectedDate?: Date;
  /** @deprecated Use `onSelectDate` instead. Fires when a date is selected. */
  onDateChange?: (event: React.SyntheticEvent<HTMLElement>, data?: { value: Date }) => void;
  /** @deprecated Use `allowTextInput` instead. Whether to allow manual date input. */
  allowManualInput?: boolean;
  /** @deprecated Use `formatDate` instead. Custom date formatting function. */
  formatMonthDayYear?: (date: Date) => string;
  /** @deprecated Not supported in v9. Previously set dateRangeType for week selection. */
  dateRangeType?: number;
  /** @deprecated Use root slot or clearable approach. Northstar input shorthand. */
  input?: { clearable?: boolean } | Record<string, unknown>;
  /** @deprecated Use `readOnly` prop instead. */
  readOnly?: boolean;
  /** @deprecated Use `calendar.restrictedDates` instead. Northstar restricted dates. */
  restrictedDates?: Date[];
  /** @deprecated Use `placeholder` instead. Northstar input placeholder. */
  inputPlaceholder?: string;
  /** @deprecated Not supported in v9. Northstar inputOnly mode. */
  inputOnly?: boolean;
  /** @deprecated Not supported in v9. Northstar buttonOnly mode. */
  buttonOnly?: boolean;
  /** @deprecated Not supported in v9. Northstar popup shorthand. */
  popup?: Record<string, unknown>;
}

/**
 * A wrapped Fluent UI v9 DatePicker which provides localized
 * strings automatically based on the given i18n-Provider.
 *
 * Accepts both legacy northstar-style props and native v9 DatePicker props
 * for backward compatibility during migration.
 *
 * @param props - LocalizedDatePickerProps
 * @return {*}
 */
export const LocalizedDatePicker: FC<LocalizedDatePickerProps> = ({
  clearable: _clearable,
  appearance,
  selectedDate,
  defaultSelectedDate,
  onDateChange,
  allowManualInput,
  formatMonthDayYear,
  dateRangeType: _dateRangeType,
  input,
  restrictedDates,
  inputPlaceholder,
  inputOnly: _inputOnly,
  buttonOnly: _buttonOnly,
  popup: _popup,
  ...props
}) => {
  const localizedStrings = useLocalizedDatepicker();
  const themedAppearance = useInputAppearance();

  // Build CalendarStrings from the localized hook output
  const calendarStrings: CalendarStrings | undefined = useMemo(() => {
    if (!localizedStrings) {
      return;
    }
    return {
      months: localizedStrings.months,
      shortMonths: localizedStrings.shortMonths,
      days: localizedStrings.days,
      shortDays: localizedStrings.shortDays,
      goToToday: localizedStrings.goToToday,
      prevMonthAriaLabel: localizedStrings.prevMonthAriaLabel,
      nextMonthAriaLabel: localizedStrings.nextMonthAriaLabel,
      prevYearAriaLabel: localizedStrings.prevYearAriaLabel,
      nextYearAriaLabel: localizedStrings.nextYearAriaLabel,
      prevYearRangeAriaLabel: localizedStrings.prevYearRangeAriaLabel,
      nextYearRangeAriaLabel: localizedStrings.nextYearRangeAriaLabel,
      closeButtonAriaLabel: localizedStrings.closeButtonAriaLabel,
      weekNumberFormatString: localizedStrings.weekNumberFormatString,
      selectedDateFormatString: localizedStrings.selectedDateFormatString,
      todayDateFormatString: localizedStrings.todayDateFormatString,
      monthPickerHeaderAriaLabel: localizedStrings.monthPickerHeaderAriaLabel,
      yearPickerHeaderAriaLabel: localizedStrings.yearPickerHeaderAriaLabel,
    };
  }, [localizedStrings]);

  // Default localized date formatter: "DD. MonthName YYYY" (e.g. "25. Februar 2026")
  const defaultFormatDate = useCallback(
    (date?: Date) => {
      if (!date) return '';
      const day = date.getDate().toString().padStart(2, '0');
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const monthName = localizedStrings?.months[monthIndex] ?? (monthIndex + 1).toString();
      return `${day}. ${monthName} ${year}`;
    },
    [localizedStrings],
  );

  // Map legacy props to v9 equivalents
  const value = props.value ?? selectedDate ?? defaultSelectedDate ?? null;
  const allowTextInput = props.allowTextInput ?? allowManualInput;
  const formatDate =
    props.formatDate ??
    (formatMonthDayYear
      ? (date?: Date) => (date ? formatMonthDayYear(date) : '')
      : defaultFormatDate);
  const placeholder = props.placeholder ?? inputPlaceholder ?? localizedStrings?.inputPlaceholder;

  // Map the legacy northstar `restrictedDates` onto the v9 `calendar` slot. Props given
  // through the slot always win, and a slot holding a custom element is left untouched.
  const calendar = useMemo(() => {
    if (!restrictedDates?.length) {
      return props.calendar;
    }
    if (props.calendar === undefined || props.calendar === null) {
      return { restrictedDates };
    }
    if (!isPlainObject(props.calendar)) {
      return props.calendar;
    }
    return { restrictedDates, ...props.calendar };
  }, [props.calendar, restrictedDates]);

  // Map legacy onDateChange to v9 onSelectDate
  const handleSelectDate = (date: Date | null | undefined) => {
    if (onDateChange) {
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.SyntheticEvent<HTMLElement>;
      if (date) {
        onDateChange(syntheticEvent, { value: date });
      } else {
        onDateChange(syntheticEvent);
      }
    }
    props.onSelectDate?.(date);
  };

  return (
    <div className="localizedDatePicker">
      <DatePicker
        appearance={appearance ?? themedAppearance}
        isMonthPickerVisible={false}
        {...props}
        calendar={calendar}
        value={value}
        allowTextInput={allowTextInput}
        formatDate={formatDate}
        placeholder={placeholder}
        strings={calendarStrings}
        onSelectDate={handleSelectDate}
      />
    </div>
  );
};
